import { useCallback, useEffect, useState } from "react";

import { listFlows } from "@/api/flows";
import { Flow } from "@/types";
import { FlowStatusSnapshot } from "@/types/stats";

export interface ServiceStatusItem {
  flow: Flow;
  stats24h: FlowStatusSnapshot | null;
  stats7d: FlowStatusSnapshot | null;
  stats30d: FlowStatusSnapshot | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseServiceStatusResult {
  items: ServiceStatusItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetch every flow with pre-computed 24h/7d/30d status snapshots.
 *
 * The backend aggregates step_daily_stats per flow and returns the snapshots
 * in a single paginated call, avoiding 3N separate stats requests.
 */
export function useServiceStatus(): UseServiceStatusResult {
  const [items, setItems] = useState<ServiceStatusItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const flowsResponse = await listFlows({ limit: 500, offset: 0, includeStatus: true });
      const flows = flowsResponse.items;

      const enriched: ServiceStatusItem[] = flows.map((flow) => ({
        flow,
        stats24h: flow.status?.["1d"] ?? null,
        stats7d: flow.status?.["7d"] ?? null,
        stats30d: flow.status?.["30d"] ?? null,
        isLoading: false,
        error: null,
      }));

      setItems(enriched);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load flows");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { items, isLoading, error, refresh: fetchAll };
}
